import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface Message {
  id: number;
  role: 'ai' | 'user' | 'system';
  text: string;
  options?: { label: string; value: string }[];
}

const SITES = [
  { name: 'Fresh Bakery', status: 'live', initial: 'FB' },
  { name: 'My Portfolio', status: 'draft', initial: 'MP' },
];

let msgId = 0;
const msg = (role: Message['role'], text: string, options?: Message['options']): Message => ({
  id: ++msgId,
  role,
  text,
  options,
});

export default function ConciergeView() {
  const [messages, setMessages] = useState<Message[]>([
    msg('ai', "Hi Sarah! I'm your WiserWeb assistant. What would you like to do today?", [
      { label: 'Build a new site', value: 'new' },
      { label: 'Migrate an existing site', value: 'migrate' },
      { label: 'Freshen up a site', value: 'facelift' },
    ]),
  ]);
  const [showPreview, setShowPreview] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const [typing, setTyping] = useState(false);
  const [step, setStep] = useState(0);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [activeSite, setActiveSite] = useState<string | null>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  useEffect(scrollToBottom, [messages, typing, scrollToBottom]);

  const addAiMessage = useCallback(
    (text: string, options?: Message['options'], delay = 1200) => {
      setTyping(true);
      setTimeout(() => {
        setTyping(false);
        setMessages((prev) => [...prev, msg('ai', text, options)]);
      }, delay);
    },
    [],
  );

  const handleOption = (value: string, label: string) => {
    setMessages((prev) => {
      // Remove options from the last AI message
      const updated = prev.map((m, i) =>
        i === prev.length - 1 ? { ...m, options: undefined } : m,
      );
      return [...updated, msg('user', label)];
    });

    if (step === 0) {
      // Goal selection
      setStep(1);
      if (value === 'new') {
        addAiMessage(
          "Exciting! Tell me about your new site. What's it for? A business, portfolio, online store, blog...?",
        );
      } else if (value === 'migrate') {
        addAiMessage(
          "Sure! Paste the URL of the site you'd like to move to WiserWebCloud. I'll analyze it and create a plan.",
        );
      } else {
        addAiMessage(
          "Great choice! Which of your sites needs a refresh? Or paste a URL and I'll take a look.",
          SITES.map((s) => ({ label: s.name, value: s.name })),
        );
      }
    } else if (step === 1) {
      setStep(2);
      addAiMessage(
        "Got it! Do you already have branding (logo, colors, fonts) or would you like me to create some?",
        [
          { label: 'I have branding', value: 'has_brand' },
          { label: 'Help me create branding', value: 'need_brand' },
          { label: 'Keep it simple for now', value: 'skip_brand' },
        ],
      );
    } else if (step === 2) {
      setStep(3);
      if (value === 'need_brand') {
        addAiMessage(
          "I'll design some options for you! What vibe are you going for? Pick one or describe your own.",
          [
            { label: 'Modern & minimal', value: 'modern' },
            { label: 'Warm & friendly', value: 'warm' },
            { label: 'Bold & vibrant', value: 'bold' },
          ],
        );
      } else {
        addAiMessage(
          "Perfect. I'm generating your site now. This'll take just a moment...",
          undefined,
          800,
        );
        setTimeout(() => {
          setShowPreview(true);
          setActiveSite('Fresh Bakery');
          setMessages((prev) => [
            ...prev,
            msg('system', 'Backup saved: v1 — initial generation'),
          ]);
          addAiMessage(
            "Your site is ready! Take a look at the preview. Here's what I'd suggest to make it even better:",
            undefined,
            2400,
          );
          setTimeout(() => {
            addAiMessage(
              "1. Add customer testimonials for trust\n2. Include your opening hours on the homepage\n3. Add a Google Maps embed for directions\n\nWant me to apply any of these?",
              [
                { label: 'Apply all suggestions', value: 'apply_all' },
                { label: 'Let me pick which ones', value: 'pick' },
                { label: 'Looks great as-is!', value: 'done' },
              ],
              1800,
            );
          }, 2600);
        }, 1500);
      }
    } else if (step === 3) {
      setStep(4);
      addAiMessage(
        "Perfect. I'm generating your site now. This'll take just a moment...",
        undefined,
        800,
      );
      setTimeout(() => {
        setShowPreview(true);
        setActiveSite('Fresh Bakery');
        setMessages((prev) => [
          ...prev,
          msg('system', 'Backup saved: v1 — initial generation'),
        ]);
        addAiMessage(
          "Done! Your site is live at fresh-bakery.wiserwebhost.com. Want me to keep improving it, or are you happy with this version?",
          [
            { label: 'Keep improving', value: 'improve' },
            { label: 'Publish it!', value: 'publish' },
          ],
          2400,
        );
      }, 1500);
    } else {
      addAiMessage(
        "Working on it! I'll save a backup of the current version first.",
        undefined,
        800,
      );
      setTimeout(() => {
        setMessages((prev) => [...prev, msg('system', 'Backup saved: v2')]);
        addAiMessage(
          "Done! I've applied the changes. Check the preview to see the updated site. Anything else?",
        );
      }, 2000);
    }
  };

  const handleSend = () => {
    if (!inputVal.trim()) return;
    const text = inputVal.trim();
    setInputVal('');
    setMessages((prev) => [...prev, msg('user', text)]);
    addAiMessage(
      "Got it! Let me work on that. I'll save a backup of your current version first, then make the changes.",
    );
    setTimeout(() => {
      setMessages((prev) => [...prev, msg('system', `Backup saved: v${step + 1}`)]);
    }, 2000);
  };

  return (
    <div className="h-screen bg-base flex overflow-hidden">
      {/* Left rail — sites list */}
      <div className="w-16 md:w-56 border-r border-zinc-800 flex flex-col bg-zinc-950/50 shrink-0">
        <div className="h-14 flex items-center px-3 md:px-4 border-b border-zinc-800">
          <span className="text-orange-500 font-bold text-lg hidden md:block">WiserWeb</span>
          <span className="text-orange-500 font-bold text-lg md:hidden">W</span>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          <button className="w-full flex items-center gap-2 px-3 md:px-4 py-2.5 text-orange-400 hover:bg-zinc-800/50 transition text-sm">
            <span className="material-icons text-lg">add</span>
            <span className="hidden md:inline">New Site</span>
          </button>

          <div className="mt-1 border-t border-zinc-800/50">
            {SITES.map((site) => (
              <button
                key={site.name}
                onClick={() => setActiveSite(site.name)}
                className={`w-full flex items-center gap-2 px-3 md:px-4 py-2.5 transition text-sm ${
                  activeSite === site.name
                    ? 'bg-orange-500/10 text-white border-l-2 border-orange-500'
                    : 'text-zinc-400 hover:bg-zinc-800/50'
                }`}
              >
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-orange-500/30 to-red-500/30 flex items-center justify-center text-xs font-bold text-orange-400 shrink-0">
                  {site.initial}
                </div>
                <div className="hidden md:block text-left min-w-0">
                  <p className="truncate">{site.name}</p>
                  <p className="text-[10px] text-zinc-600">
                    {site.status === 'live' ? '● Live' : '○ Draft'}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="p-3 border-t border-zinc-800">
          <Link
            to="/mockups"
            className="flex items-center gap-2 text-xs text-zinc-500 hover:text-zinc-300 transition"
          >
            <span className="material-icons text-sm">arrow_back</span>
            <span className="hidden md:inline">Back to Gallery</span>
          </Link>
        </div>
      </div>

      {/* Center — Chat */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="h-14 flex items-center justify-between px-4 md:px-6 border-b border-zinc-800 shrink-0">
          <h1 className="text-sm font-medium text-white">AI Concierge</h1>
          <div className="flex items-center gap-2">
            {showPreview && (
              <span className="text-xs text-emerald-400 flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                Site live
              </span>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 space-y-4">
          {messages.map((m) => {
            if (m.role === 'system') {
              return (
                <div key={m.id} className="flex justify-center">
                  <span className="text-[11px] text-zinc-600 bg-zinc-800/50 px-3 py-1 rounded-full">
                    {m.text}
                  </span>
                </div>
              );
            }
            return (
              <div
                key={m.id}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-3 ${
                    m.role === 'user'
                      ? 'bg-orange-500/15 border border-orange-500/30 rounded-br-md'
                      : 'bg-zinc-800/60 border border-zinc-700/50 rounded-bl-md'
                  }`}
                >
                  {m.role === 'ai' && (
                    <span className="material-icons text-orange-400 text-xs mr-1 align-middle">
                      smart_toy
                    </span>
                  )}
                  <span
                    className={`text-sm whitespace-pre-line ${
                      m.role === 'user' ? 'text-orange-200' : 'text-zinc-300'
                    }`}
                  >
                    {m.text}
                  </span>
                  {m.options && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {m.options.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => handleOption(opt.value, opt.label)}
                          className="px-3 py-1.5 bg-zinc-700/50 hover:bg-orange-500/20 border border-zinc-600 hover:border-orange-500/50 rounded-lg text-xs text-zinc-300 hover:text-orange-300 transition"
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {typing && (
            <div className="flex justify-start">
              <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" />
                  <span
                    className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce"
                    style={{ animationDelay: '150ms' }}
                  />
                  <span
                    className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce"
                    style={{ animationDelay: '300ms' }}
                  />
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-zinc-800 shrink-0">
          <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/50 rounded-xl px-3">
            <input
              type="text"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="flex-1 bg-transparent py-3 text-sm text-white placeholder:text-zinc-600 outline-none"
            />
            <button
              onClick={handleSend}
              className="text-orange-400 hover:text-orange-300 transition p-1"
            >
              <span className="material-icons text-xl">send</span>
            </button>
          </div>
        </div>
      </div>

      {/* Right — Preview panel */}
      <div
        className={`border-l border-zinc-800 bg-zinc-950/30 transition-all duration-500 overflow-hidden shrink-0 ${
          showPreview ? 'w-0 md:w-[380px] lg:w-[440px]' : 'w-0'
        }`}
      >
        <div className="w-[380px] lg:w-[440px] h-full flex flex-col">
          <div className="h-14 flex items-center justify-between px-4 border-b border-zinc-800 shrink-0">
            <span className="text-sm font-medium text-white">Preview</span>
            <div className="flex gap-1">
              <button className="p-1 text-zinc-500 hover:text-white transition">
                <span className="material-icons text-sm">phone_iphone</span>
              </button>
              <button className="p-1 text-orange-400">
                <span className="material-icons text-sm">laptop</span>
              </button>
            </div>
          </div>

          <div className="flex-1 p-4 overflow-y-auto">
            {/* Mock browser */}
            <div className="bg-zinc-800/80 border border-zinc-700/60 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-700/40 bg-zinc-900/50">
                <div className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-500/60" />
                  <span className="w-2 h-2 rounded-full bg-yellow-500/60" />
                  <span className="w-2 h-2 rounded-full bg-green-500/60" />
                </div>
                <div className="flex-1 flex items-center gap-1 bg-zinc-800/80 rounded px-2 py-0.5">
                  <span className="material-icons text-green-400 text-[10px]">lock</span>
                  <span className="text-[10px] text-zinc-500 font-mono truncate">
                    fresh-bakery.wiserwebhost.com
                  </span>
                </div>
              </div>

              {/* Fake site content */}
              <div className="p-4 space-y-3">
                <div className="h-24 bg-gradient-to-br from-orange-600/20 to-amber-600/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg font-bold text-orange-300/60">Fresh Bakery</span>
                </div>
                <div className="h-3 bg-zinc-700/30 rounded w-3/4" />
                <div className="h-3 bg-zinc-700/30 rounded w-1/2" />
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <div className="h-16 bg-zinc-700/20 rounded" />
                  <div className="h-16 bg-zinc-700/20 rounded" />
                  <div className="h-16 bg-zinc-700/20 rounded" />
                </div>
                <div className="h-3 bg-zinc-700/30 rounded w-5/6" />
                <div className="h-3 bg-zinc-700/30 rounded w-2/3" />
              </div>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="text-[10px] px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400">
                Live
              </span>
              <span className="text-[10px] px-2 py-1 bg-sky-500/10 border border-sky-500/30 rounded-full text-sky-400">
                SSL active
              </span>
              <span className="text-[10px] px-2 py-1 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400">
                3 backups
              </span>
            </div>

            {/* Version history */}
            <div className="mt-4">
              <h4 className="text-xs font-medium text-zinc-500 mb-2">Version History</h4>
              <div className="space-y-1">
                {['v1 — Initial generation', 'v2 — Added testimonials', 'v3 — Updated hero'].map(
                  (v, i) => (
                    <div
                      key={v}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${
                        i === 2
                          ? 'bg-orange-500/10 text-orange-300 border border-orange-500/20'
                          : 'text-zinc-500 hover:bg-zinc-800/50 cursor-pointer'
                      }`}
                    >
                      <span className="material-icons text-xs">
                        {i === 2 ? 'radio_button_checked' : 'radio_button_unchecked'}
                      </span>
                      {v}
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
